import type { CategorySplitConfig, CategorySplitRow, CategoryStyle, ClassificationConfig, SecondaryClassificationConfig, SecondaryLineStyle, SplitPart } from '../presentation/config';

/**
 * Linked dual classification (V1.8.1): the principal classification (A) fills, a secondary one (B) outlines the SAME
 * entities. This module holds the pure pieces: the explicit «Desglosar categorías» table, the outline palette, the
 * validation of stored definitions and the A × B coverage. Nothing here reads or writes the Maestro, Excel or project
 * fields: a split value is resolved on the fly from the raw value and the table.
 */

/** Outline colours: saturated and dark enough to read over satellite imagery and over the qualitative fill palette. */
export const SECONDARY_OUTLINE_PALETTE = ['#c2185b', '#1a237e', '#ff6f00', '#004d40', '#6a1b9a', '#212121', '#8d6e63', '#00838f', '#9e9d24', '#ad1457'] as const;
/** Readability limit of outline categories (more is allowed, with a warning). */
export const SECONDARY_READABLE_CATEGORIES = 8;
export const SECONDARY_WIDTH = { min: 1.5, max: 6, default: 3 } as const;
export const SPLIT_TEXT_MAX = 120;
export const SPLIT_NAME_MAX = 60;
export const SPLIT_ROWS_MAX = 1000;
const NO_VALUE = 'Sin valor';

const HEX = /^#[0-9a-f]{6}$/i;
const validColor = (value: unknown): string | undefined => typeof value === 'string' && HEX.test(value.trim()) ? value.trim().toLowerCase() : undefined;
const text = (value: unknown, max: number): string | undefined => typeof value === 'string' ? value.trim().slice(0, max) : undefined;
const object = (value: unknown): Record<string, unknown> | null => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const part = (value: unknown): SplitPart | undefined => value === 'primary' || value === 'secondary' ? value : undefined;

/** Source value as matched against the table: its text, trimmed (never cut at separators). */
export const splitKey = (raw: unknown): string | null => {
  if (raw === null || raw === undefined) return null;
  const key = String(raw).trim();
  return key ? key : null;
};

const indexCache = new WeakMap<CategorySplitConfig, ReadonlyMap<string, CategorySplitRow>>();
const rowIndex = (split: CategorySplitConfig): ReadonlyMap<string, CategorySplitRow> => {
  let index = indexCache.get(split);
  if (!index) { index = new Map(split.rows.map((row) => [row.value, row])); indexCache.set(split, index); }
  return index;
};

/** The part of a combined value per the explicit table; undefined (= «Sin valor») when the value or its part is not mapped. */
export function splitValue(raw: unknown, split: CategorySplitConfig | undefined, splitPart: SplitPart): string | undefined {
  const key = split ? splitKey(raw) : null;
  if (!split || key === null) return undefined;
  const value = rowIndex(split).get(key)?.[splitPart].trim();
  return value ? value : undefined;
}

/** Whether a classification dimension reads a part of the split table (and that table belongs to its field). */
export const readsSplit = (field: string | null | undefined, splitPart: SplitPart | undefined, split: CategorySplitConfig | undefined): split is CategorySplitConfig =>
  Boolean(field && splitPart && split && split.sourceField === field);

/** Value the principal classification uses for one raw value. */
export const principalValue = (raw: unknown, config: Pick<ClassificationConfig, 'field' | 'splitPart' | 'categorySplit'>): unknown =>
  readsSplit(config.field, config.splitPart, config.categorySplit) ? splitValue(raw, config.categorySplit, config.splitPart!) : raw;

/** Value the secondary classification uses for one raw value of its own field. */
export const secondaryValue = (raw: unknown, config: Pick<ClassificationConfig, 'secondary' | 'categorySplit'>): unknown =>
  readsSplit(config.secondary?.field, config.secondary?.splitPart, config.categorySplit) ? splitValue(raw, config.categorySplit, config.secondary!.splitPart!) : raw;

/** Source values present in the data that the table does not cover (shown as «Sin valor» and reported to the user). */
export function uncoveredSplitValues(values: Iterable<unknown>, split: CategorySplitConfig): readonly string[] {
  const index = rowIndex(split); const missing = new Set<string>();
  for (const value of values) { const key = splitKey(value); if (key !== null && !index.has(key)) missing.add(key); }
  return [...missing].sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
}

export const defaultSecondaryConfig = (field: string | null, name: string, splitPart?: SplitPart): SecondaryClassificationConfig => ({
  enabled: true, field, ...(splitPart ? { splitPart } : {}), name, categories: [], line: 'solid', width: SECONDARY_WIDTH.default,
});

/** Validated copy of a stored split table (any version, hand-edited files): malformed rows are dropped, never guessed. */
export function normalizeCategorySplit(value: unknown): CategorySplitConfig | undefined {
  const source = object(value); const sourceField = text(source?.sourceField, 400);
  if (!source || !sourceField || !Array.isArray(source.rows)) return undefined;
  const seen = new Set<string>();
  const rows = source.rows.slice(0, SPLIT_ROWS_MAX).flatMap((item): CategorySplitRow[] => {
    const row = object(item); const key = splitKey(typeof row?.value === 'string' ? row.value.slice(0, SPLIT_TEXT_MAX) : null);
    if (!row || key === null || seen.has(key)) return [];
    seen.add(key);
    return [{ value: key, primary: text(row.primary, SPLIT_TEXT_MAX) ?? '', secondary: text(row.secondary, SPLIT_TEXT_MAX) ?? '' }];
  });
  return { sourceField, primaryName: text(source.primaryName, SPLIT_NAME_MAX) || 'Parte 1', secondaryName: text(source.secondaryName, SPLIT_NAME_MAX) || 'Parte 2', rows };
}

const normalizeCategories = (value: unknown): readonly CategoryStyle[] => Array.isArray(value)
  ? value.slice(0, 500).flatMap((item): CategoryStyle[] => {
    const category = object(item); const raw = typeof category?.value === 'string' ? category.value : null; const color = validColor(category?.color);
    if (!category || raw === null || !color) return [];
    return [{ value: raw, label: text(category.label, SPLIT_TEXT_MAX) || raw, color, visible: category.visible !== false }];
  })
  : [];

/** Validated copy of a stored secondary classification; undefined when there is nothing usable. */
export function normalizeSecondaryClassification(value: unknown): SecondaryClassificationConfig | undefined {
  const source = object(value); if (!source) return undefined;
  const field = typeof source.field === 'string' && source.field ? source.field : null;
  const width = typeof source.width === 'number' && Number.isFinite(source.width) ? Math.min(SECONDARY_WIDTH.max, Math.max(SECONDARY_WIDTH.min, source.width)) : SECONDARY_WIDTH.default;
  const line: SecondaryLineStyle = source.line === 'dashed' ? 'dashed' : 'solid';
  const splitPart = part(source.splitPart);
  return { enabled: source.enabled === true, field, ...(splitPart ? { splitPart } : {}), name: text(source.name, SPLIT_NAME_MAX) || 'Clasificación secundaria', categories: normalizeCategories(source.categories), line, width };
}

/** The three dual fields of a stored classification, validated; the rest of the configuration is left as it is. */
export function normalizeDualClassification<T extends ClassificationConfig>(config: T): T {
  const { categorySplit, splitPart, secondary, ...rest } = config as T & Record<string, unknown>;
  const split = normalizeCategorySplit(categorySplit); const principalPart = part(splitPart); const second = normalizeSecondaryClassification(secondary);
  return { ...rest, ...(split ? { categorySplit: split } : {}), ...(principalPart ? { splitPart: principalPart } : {}), ...(second ? { secondary: second } : {}) } as T;
}

export interface DualCoverage {
  readonly total: number;
  readonly both: number;
  readonly onlyPrincipal: number;
  readonly onlySecondary: number;
  readonly none: number;
}

/** A × B data coverage of the same entities (each entity counted once, whatever its categories). */
export function dualCoverage(principal: ReadonlyMap<string, boolean>, secondary: ReadonlyMap<string, boolean>): DualCoverage {
  let both = 0; let onlyPrincipal = 0; let onlySecondary = 0; let none = 0;
  principal.forEach((hasA, id) => {
    const hasB = secondary.get(id) === true;
    if (hasA && hasB) both += 1; else if (hasA) onlyPrincipal += 1; else if (hasB) onlySecondary += 1; else none += 1;
  });
  return { total: principal.size, both, onlyPrincipal, onlySecondary, none };
}

export const SECONDARY_NO_VALUE = NO_VALUE;
